import type { CandidateAssessment, LLMEvaluation } from "@shared/labs/candidate-assessment";
import { useEffect, useState } from "react";

/** Which SSE phases have completed — drives the ProcessingView progress. */
export interface StreamProgress {
  pdfExtracted: boolean;
  classificationReady: boolean;
  profileReady: boolean;
  scoringReady: boolean;
  positionFitReady: boolean;
  marketLevelReady: boolean;
  classificationData?: unknown;
  profileData?: unknown;
  scoringData?: unknown;
  positionFitData?: unknown;
  marketLevelData?: unknown;
}

const EMPTY_PROGRESS: StreamProgress = {
  pdfExtracted: false,
  classificationReady: false,
  profileReady: false,
  scoringReady: false,
  positionFitReady: false,
  marketLevelReady: false,
};

interface PhaseDef {
  key: string;
  gerund: string;
  done: string;
  isDone: (p: StreamProgress) => boolean;
}

const PHASES: PhaseDef[] = [
  {
    key: "extract",
    gerund: "Lendo perfil",
    done: "Perfil lido",
    isDone: (p) => p.pdfExtracted || p.classificationReady,
  },
  {
    key: "classify",
    gerund: "Classificando",
    done: "Classificada",
    isDone: (p) => p.classificationReady,
  },
  {
    key: "profile",
    gerund: "Construindo perfil",
    done: "Perfil construído",
    isDone: (p) => p.profileReady,
  },
  {
    key: "score",
    gerund: "Pontuando skills",
    done: "Skills pontuadas",
    isDone: (p) => p.scoringReady,
  },
  {
    key: "fit",
    gerund: "Avaliando fit com a posição",
    done: "Fit avaliado",
    isDone: (p) => p.positionFitReady,
  },
  {
    key: "market",
    gerund: "Calibrando nível de mercado",
    done: "Nível calibrado",
    isDone: (p) => p.marketLevelReady,
  },
];

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function Ellipsis({ reduced }: { reduced: boolean }) {
  if (reduced) return <span aria-hidden>…</span>;
  return (
    <span aria-hidden className="ell">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </span>
  );
}

export function ProcessingView({
  resultReady,
  streamProgress,
  onShowResults,
  onCancel,
}: {
  assessment: CandidateAssessment;
  resultReady: boolean;
  extractedTextPreview?: string;
  extractedCharCount?: number;
  llmEvaluation?: LLMEvaluation | null;
  streamProgress?: StreamProgress;
  onShowResults: () => void;
  onCancel?: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const progress = streamProgress ?? EMPTY_PROGRESS;

  // Auto-advance to results once the pipeline finishes — no extra click.
  // Short delay lets the "Concluído" state register visually before the
  // view swaps, so the transition doesn't feel abrupt.
  useEffect(() => {
    if (!resultReady) return;
    const t = setTimeout(onShowResults, 450);
    return () => clearTimeout(t);
  }, [resultReady, onShowResults]);

  // Phases complete in parallel via SSE, but we display them strictly in
  // order: a phase only reads as "done" once every earlier phase is done too.
  // Avoids the jarring effect of Etapa 6 finishing before Etapa 5.
  let doneCount = 0;
  for (const phase of PHASES) {
    if (!phase.isDone(progress)) break;
    doneCount++;
  }
  const totalCount = PHASES.length;
  const pct = resultReady ? 100 : Math.round((doneCount / totalCount) * 100);
  const activeIdx = resultReady ? -1 : doneCount < totalCount ? doneCount : -1;

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-mid fade-up" style={{ width: "100%" }} data-testid="labs-ci-processing">
        <div style={{ marginBottom: 20 }}>
          <p className="ci-eyebrow">Avaliação · em andamento</p>
          <h1 className="ci-title">{resultReady ? "Avaliação pronta." : "Avaliando candidato."}</h1>
          <p className="ci-sub">
            {resultReady
              ? "Pronto para revisar o perfil, skills e fit com a posição."
              : "Não feche esta aba até concluir."}
          </p>
        </div>

        <div className="card">
          {/* thin progress strip at the top of the card */}
          <div className={`progress${resultReady ? "" : doneCount === 0 ? " indet" : ""}`}>
            <div
              className="bar"
              style={resultReady || doneCount > 0 ? { width: `${pct}%` } : undefined}
            />
          </div>

          <div className="card-head">
            <div className="card-head-left">
              <h2>{resultReady ? "Pipeline concluído" : "Avaliando perfil"}</h2>
              <div className="sub">
                {resultReady
                  ? "Todas as fases foram concluídas com sucesso."
                  : `Fase ${Math.min(doneCount + 1, totalCount)} de ${totalCount}`}
              </div>
            </div>
            <div className="card-head-right">
              <span className={`pill ${resultReady ? "forest" : "coral"} no-dot`}>
                {resultReady ? "Concluído" : `${pct}%`}
              </span>
            </div>
          </div>

          <div className="card-body">
            {PHASES.map((p, i) => {
              const done = resultReady || i < doneCount;
              const active = i === activeIdx;

              let rightCls = "pending";
              let rightContent: React.ReactNode;
              if (done) {
                rightCls = "done";
                rightContent = p.done;
              } else if (active) {
                rightCls = "active";
                rightContent = (
                  <>
                    {p.gerund}
                    <Ellipsis reduced={reducedMotion} />
                  </>
                );
              } else {
                rightContent = p.gerund;
              }

              return (
                <div key={p.key} className="phase-row">
                  <span className="left">Etapa {i + 1}</span>
                  <span className={`right ${rightCls}`}>{rightContent}</span>
                </div>
              );
            })}
          </div>

          <div className="card-foot">
            <div style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
              {resultReady ? "Avaliação completa" : null}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {resultReady ? (
                <button
                  type="button"
                  onClick={onShowResults}
                  data-testid="labs-ci-show-results"
                  className="btn primary"
                >
                  Ver resultado
                </button>
              ) : (
                onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    data-testid="labs-ci-processing-cancel"
                    className="btn"
                  >
                    Cancelar
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
